// Query parsing and search ordering — pure functions with no imports, so the
// rules below can be unit-tested without a network or a DOM
// (scripts/test-search-ranking.mjs).
//
// Why this exists: TMDB returns search results in its own relevance/popularity
// order, which buries exactly the titles a person typed. Typing "boys" listed
// "Boys Over Flowers", "The Boys", "Big Boys" … but never the 2003 Tamil film
// *Boys* that the library actually hosts (47 votes, popularity 2.2 — TMDB puts
// it hundreds of pages deep). The site's ordering is therefore:
//
//   1. the title IS the query            ("Boys", "Boys Power" for "boys power")
//   2. the title STARTS WITH the query   ("Boys Over Flowers", "Boys Power")
//   3. every query word starts a word    ("The Boys", "Big Boys", "Nickel Boys")
//   4. every query word appears anywhere ("Bad Boys: Ride or Die" is tier 3 too)
//   5. only some query words appear      (loosest match, never dropped)
//
// A trailing year is not a word: "boys 2003" means the 2003 film *Boys*, so the
// year is split off and matched against the result's release year, and it wins
// the tie inside a tier.

export const NO_POS = 99;

const stripDiacritics = (s) => String(s || "").normalize("NFD").replace(/\p{Diacritic}/gu, "");

/** Lowercase, unaccented, punctuation-free title text ("Boys Over Flowers"). */
export function normTitle(s) {
  return stripDiacritics(s)
    .toLowerCase()
    .replace(/[''`]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * "boys 2003" -> { phrase: "boys", terms: ["boys"], year: 2003 }.
 * A bare number ("2001") stays a query word, because that is how the film
 * "2001: A Space Odyssey" is searched for.
 */
export function parseSearchQuery(query) {
  const normalized = normTitle(query);
  const m = /^(.*?)\s+(1[89]\d{2}|20\d{2})$/.exec(normalized);
  const year = m ? Number(m[2]) : null;
  const terms = (m ? m[1] : normalized).split(" ").filter(Boolean);
  return { phrase: terms.join(" "), terms, year };
}

/** Release year of a card, or null — the card keeps TMDB's "YYYY-MM-DD" date. */
export function yearOfCard(card) {
  const date = String(card?.date || "");
  return /^\d{4}/.test(date) ? Number(date.slice(0, 4)) : null;
}

/**
 * Where a title sits in the ordering, or null when the query does not match it
 * at all. `pos` breaks ties inside a tier by how early the match appears.
 */
export function searchMatch(title, terms, phrase) {
  const t = normTitle(title);
  if (!t || !terms.length) return null;
  const words = t.split(" ").filter(Boolean);
  const posOf = (q) => {
    let best = NO_POS;
    for (let i = 0; i < words.length; i++) if (words[i].startsWith(q) && i < best) best = i;
    return best;
  };
  if (t === phrase) return { rank: 0, pos: 0 };
  if (t.startsWith(phrase)) return { rank: 1, pos: 0 };
  if (terms.every((q) => words.some((w) => w.startsWith(q)))) {
    return { rank: 2, pos: terms.reduce((acc, q) => acc + posOf(q), 0) };
  }
  if (terms.every((q) => t.includes(q))) return { rank: 3, pos: t.indexOf(terms[0]) };
  if (terms.some((q) => t.includes(q))) return { rank: 4, pos: NO_POS };
  return null;
}

const libraryKey = (card) => `${card.contentType}:${card.tmdbId}`;

// Normally a trailing year is matched against release dates. When nothing of
// that name was released that year, the number was part of the name instead
// ("Blade Runner 2049"), so the caller ranks the query as plain text.
function interpretation(query, yearIsName) {
  if (!yearIsName) return parseSearchQuery(query);
  const terms = normTitle(query).split(" ").filter(Boolean);
  return { phrase: terms.join(" "), terms, year: null };
}

/**
 * Re-orders raw cards to the rules above. `libraryKeys` (a Set of
 * "MOVIE:75076" style keys, or an empty/omitted Set) marks the titles PoKu
 * hosts: inside a tier those are shown first, so a file the user can actually
 * watch leads a tier it shares with something unwatchable.
 */
export function rankSearchResults(cards, query, { libraryKeys = null, yearIsName = false } = {}) {
  const { phrase, terms, year } = interpretation(query, yearIsName);
  if (!terms.length) return cards;
  const inYear = (card) => (year && yearOfCard(card) === year ? 1 : 0);
  const inLibrary = (card) => (libraryKeys && libraryKeys.has(libraryKey(card)) ? 1 : 0);
  return cards
    .map((c) => ({ c, m: searchMatch(c.title, terms, phrase) }))
    .filter((x) => x.m)
    .sort(
      (a, b) =>
        a.m.rank - b.m.rank ||
        inYear(b.c) - inYear(a.c) ||
        inLibrary(b.c) - inLibrary(a.c) ||
        a.m.pos - b.m.pos ||
        (b.c.rating || 0) - (a.c.rating || 0) ||
        (b.c.popularity || 0) - (a.c.popularity || 0),
    )
    .map((x) => x.c);
}

/** Drops duplicates by type+id, keeping the first (best-ranked) copy. */
export function dedupeCards(cards) {
  const seen = new Set();
  return cards.filter((c) => {
    const key = libraryKey(c);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
